import { useState } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { ShieldCheck, Loader2, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Step 2 of login when 2FA is enabled. Receives mfa_token + method via
 * `navigate('/2fa-challenge', { state: { ... } })`. Direct visits without
 * state get bounced to /login.
 */
export const TwoFAChallengePage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { verify2fa } = useAuth();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);

  const ctx = location.state || {};
  if (!ctx.mfa_token) {
    return <Navigate to="/login" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setLoading(true);
    try {
      await verify2fa(ctx.mfa_token, code.trim());
      toast.success('Welcome back!');
      navigate('/dashboard', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-4" data-testid="twofa-challenge-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex w-12 h-12 rounded-xl bg-indigo-600 items-center justify-center mb-4">
            <ShieldCheck className="w-6 h-6 text-white" />
          </div>
          <h1 className="text-2xl font-bold tracking-tight text-zinc-900">Two-step verification</h1>
          <p className="text-zinc-500 mt-2 text-sm">
            {ctx.method === 'totp'
              ? 'Open your authenticator app and enter the 6-digit code.'
              : `We sent a 6-digit code to ${ctx.masked_email || 'your email'}.`}
          </p>
        </div>

        <Card className="border-zinc-200 shadow-sm">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="text-base">Enter your code</CardTitle>
            <CardDescription>The code expires in 10 minutes.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="code">Verification code</Label>
                <Input
                  id="code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={8}
                  placeholder="123456"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ''))}
                  className="text-center text-2xl tracking-[0.5em] font-mono"
                  required
                  data-testid="twofa-code-input"
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-indigo-600 hover:bg-indigo-700"
                disabled={loading || code.length < 6}
                data-testid="twofa-verify-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ArrowRight className="w-4 h-4 mr-2" />}
                Verify
              </Button>
              <button
                type="button"
                className="w-full text-xs text-zinc-500 hover:text-zinc-700"
                onClick={() => navigate('/login', { replace: true })}
                data-testid="twofa-cancel-btn"
              >
                Cancel and sign in with a different account
              </button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TwoFAChallengePage;
